<template>
  <section class="py-12">
    <div class="container-wrap">
      <h2 class="mb-6 text-2xl font-bold desktop:text-3xl">
        {{ contextData.sectionTitle }}
      </h2>
      <div class="desktop:flex desktop:items-center desktop:justify-between">
        <div class="mb-5 desktop:mb-0 desktop:w-1/2 desktop:pr-8">
          <ul>
            <li
              v-for="(item, index) in contextData.cards"
              :key="item.cta"
              class="border-b border-solid border-gray-400 py-4"
            >
              <div class="flex items-center">
                <div class="flex flex-1 items-center tablet:items-start">
                  <figure
                    class="h-12 w-12 basis-12 tablet:h-20 tablet:w-20 tablet:basis-20"
                  >
                    <img
                      :src="provideImgUrl(item.imgUrl)"
                      :alt="item.cta"
                      loading="lazy"
                      class="pointer-events-none block h-full w-full object-cover"
                    />
                  </figure>
                  <div class="flex-1 pl-3 pr-2 tablet:pl-5">
                    <h3 class="text-lg font-bold">{{ item.cta }}</h3>
                    <p class="text-sm text-gray-600 tablet:text-base">
                      {{ item.description }}
                    </p>
                  </div>
                </div>
                <TheNuxtLink
                  :id="provideAcnTrackingID(index)"
                  :to="item.url"
                  :title="item.cta"
                  class="h-5 w-5 text-blue-400 hover:text-blue-500"
                >
                  <IconArrow direction="right" />
                </TheNuxtLink>
              </div>
            </li>
          </ul>
        </div>
        <div class="desktop:w-1/2">
          <LightboxVideo
            :video-data="video"
            class="aspect-video w-full rounded"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import EN_AMERICAS_JSON from '@/assets/data/home/en-americas/section-6.json';
  import EN_ASIA_PACIFIC_JSON from '@/assets/data/home/en-asia-pacific/section-6.json';
  import EN_EMEA_JSON from '@/assets/data/home/en-emea/section-6.json';
  import EN_INT_JSON from '@/assets/data/home/en-int/section-6.json';
  import EN_JP_JSON from '@/assets/data/home/en-jp/section-6.json';
  import EN_KR_JSON from '@/assets/data/home/en-kr/section-6.json';
  import EN_TW_JSON from '@/assets/data/home/en-tw/section-6.json';
  import JA_JP_JSON from '@/assets/data/home/ja-jp/section-6.json';
  import ZH_CN_JSON from '@/assets/data/home/zh-cn/section-6.json';
  import ZH_TW_JSON from '@/assets/data/home/zh-tw/section-6.json';
  import type { IContactUs } from '@/assets/types/Home';
  import type { IVideoData } from '@/components/Shared/Lightbox/Video.vue';

  const { locale } = useI18n({ useScope: 'local' });
  const { isCN } = useLanguage();
  const { provideImgUrl } = useHomeData();

  const video = computed(
    (): IVideoData => ({
      title: contextData.value.sectionTitle,
      description: contextData.value.sectionTitle,
      url: contextData.value.videoUrl,
      imageUrl: provideImgUrl(contextData.value.videoImgUrl),
      isAutoPlay: true,
    })
  );

  const contextData = computed((): IContactUs => {
    switch (locale.value) {
      case EnumLocale.EN_INT:
        return EN_INT_JSON;
      case EnumLocale.EN_AMERICAS:
        return EN_AMERICAS_JSON;
      case EnumLocale.EN_JP:
        return EN_JP_JSON;
      case EnumLocale.EN_TW:
        return EN_TW_JSON;
      case EnumLocale.EN_KR:
        return EN_KR_JSON;
      case EnumLocale.EN_ASIA_PACIFIC:
        return EN_ASIA_PACIFIC_JSON;
      case EnumLocale.EN_EMEA:
        return EN_EMEA_JSON;
      case EnumLocale.ZH_TW:
        return ZH_TW_JSON;
      case EnumLocale.ZH_CN:
        return ZH_CN_JSON;
      case EnumLocale.JA_JP:
        return JA_JP_JSON;
      default:
        return EN_INT_JSON;
    }
  });

  const provideAcnTrackingID = (index: number): string | undefined => {
    if (isCN.value) {
      switch (index) {
        case 1:
          return 'tracking_ssupport';
        case 2:
          return 'tracking_tsupport';
        default:
          return undefined;
      }
    }
    return undefined;
  };
</script>
